import React, { type ReactNode, useState } from 'react';

import { EmojiCategory } from '@pkg/hooks';

import { EmojiList, type EmojiListProps, useCategoryTitle } from '../EmojiList';

import { Navigation, type NavigationProps, Search, type SearchProps } from './components';

import {
    CategoryTitle,
    Header,
    Main,
    MainSection,
    NavigationWrapper,
    SearchWrapper,
    StyledEmojiPicker,
} from './styles';

export interface EmojiPickerProps extends Pick<EmojiListProps, 'darkMode' | 'onSelectEmoji'> {
    children?: ReactNode;
}

export function EmojiPicker({ children, darkMode = false, onSelectEmoji }: EmojiPickerProps) {
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState(EmojiCategory.Smileys);

    const categoryTitle = useCategoryTitle({ category, keywordFilter: search });
    const hasSearch = Boolean(search.length);

    const handleSearch: SearchProps['onChange'] = (value = '') => setSearch(value.toString());
    const handleCategoryChange: NavigationProps['onChange'] = (key) => setCategory(key);

    return (
        <StyledEmojiPicker $darkMode={darkMode}>
            <Header>
                <SearchWrapper>
                    <Search darkMode={darkMode} value={search} onChange={handleSearch} />
                </SearchWrapper>

                <NavigationWrapper>
                    <Navigation currentCategory={category} disabled={hasSearch} onChange={handleCategoryChange} />
                </NavigationWrapper>
            </Header>

            <Main>
                <MainSection>
                    <CategoryTitle>{categoryTitle}</CategoryTitle>
                </MainSection>

                <MainSection $scrollable>
                    <EmojiList
                        // Intentionally passing `undefined` when `hasSearch`
                        // in order to scope "filtered results" to the entire
                        // `collection` of emojis, instead of just the
                        // emojis within a specific `category`.
                        category={hasSearch ? undefined : category}
                        keywordFilter={search}
                        darkMode={darkMode}
                        onSelectEmoji={onSelectEmoji}
                    />
                </MainSection>
            </Main>

            {children}
        </StyledEmojiPicker>
    );
}
